import {
  ApolloClient,
  DataProxy,
  FetchResult,
  useApolloClient,
} from '@apollo/client';
import {
  EditAppBar,
  NotificationTypeEnum,
  useNotification,
} from '@prismamedia/one-components';
import { useEditContext } from '@prismamedia/one-crud-react';
import {
  NewsletterFragment,
  UpsertNewsletterMutation,
  UpsertNewsletterMutationVariables,
} from '__generated__/queries-newsletter';
import { UPSERT_NEWSLETTER_MUTATION } from 'apollo/mutations/upsertNewsletter.newsletter.graphql';
import moment from 'moment';
import { clone, isEmpty } from 'ramda';
import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { paths, replaceParams } from 'routing';
import { v4 as uuid } from 'uuid';
import {
  getItemInfoForMutation,
  isNewsletterValid,
  transformItem,
} from '../SaveButton/utils';

interface AppBarProps {
  isCreate: boolean;
}

export const AppBar: FC<AppBarProps> = ({ isCreate }) => {
  const context = useEditContext();
  const history = useHistory();
  const { pushNotification } = useNotification();
  const [saveLoading, setSaveLoading] = useState(false);
  const client = useApolloClient() as ApolloClient<DataProxy>;

  const beforeSave = (item: any) => {
    const validOrError = isNewsletterValid(item);
    if (validOrError !== true) {
      pushNotification({
        message: validOrError as string,
        type: NotificationTypeEnum.error,
      });
      return null;
    }

    return transformItem(item);
  };

  const saveItem = async () => {
    if (isEmpty(context.item)) {
      return;
    }

    const itemToSave = clone(context.item);
    const contextItem: NewsletterFragment = beforeSave(itemToSave);

    if (!contextItem) {
      return;
    }
    const id = !isCreate ? contextItem.id : uuid();
    const itemForMutation = getItemInfoForMutation(contextItem);
    const variables: UpsertNewsletterMutationVariables = {
      create: { ...itemForMutation, id },
      update: { ...itemForMutation },
      where: { id },
    };

    setSaveLoading(true);
    try {
      const res: FetchResult<UpsertNewsletterMutation> = await client.mutate<
        UpsertNewsletterMutation,
        UpsertNewsletterMutationVariables
      >({
        mutation: UPSERT_NEWSLETTER_MUTATION,
        variables,
      });

      if (res.data?.upsertNewsletter?.id) {
        pushNotification({
          message: 'La newsletter a bien été enregitrée',
          type: NotificationTypeEnum.success,
        });
      } else {
        pushNotification({
          message:
            "Une erreur a eu lieu lors de l'enregistrement de la newsletter",
          type: NotificationTypeEnum.error,
        });
      }

      if (isCreate && res.data?.upsertNewsletter?.id) {
        history.push(
          replaceParams(paths.NEWSLETTER_EDIT, {
            id: res.data.upsertNewsletter.id,
          }),
        );
      }
    } catch (e: any) {
      console.error(e);
      pushNotification({
        message: e.message,
        type: NotificationTypeEnum.error,
      });
    }
    setSaveLoading(false);
  };

  return (
    <EditAppBar
      onNavigateBack={() => history.push(paths.NEWSLETTER_LIST)}
      title={context.item.name}
      subTitle={moment(context.item.updatedAt).format('D MMM YYYY, HH[h]mm')}
      save={{
        onClick: saveItem,
        loading: saveLoading,
      }}
    />
  );
};
